import type { DBEquipmentBookingLine, DBEquipmentItem } from "@/lib/db-types";
import { addDays, eachDay, type DateRange } from "./availability";

/**
 * Belegg (VIB-2920, mockup G): utstyr × dager. Ren logikk over bookinglinjene
 * fra equipment_booking_lines. Dobbelbooking bruker samme regel som
 * availability.ts: sum antall på dagen er større enn det vi eier.
 */

export type OccupancyKind = "free" | "booked" | "conflict" | "service";

export interface OccupancyCell {
  day: string;
  kind: OccupancyKind;
  /** Prosjektnummer på første dag, antall for mengdevare, «⚠ 2» ved dobbelbooking. */
  label: string;
  /** Bookingen et klikk åpner. */
  bookingId: string | null;
}

type CellItem = Pick<DBEquipmentItem, "id" | "quantity" | "status">;
type CellLine = Pick<DBEquipmentBookingLine, "id" | "booking_id" | "item_id" | "quantity" | "date_from" | "date_to" | "project_code" | "title">;

const WEEKDAYS = ["sø", "ma", "ti", "on", "to", "fr", "lø"];

function utcDay(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

/** Mandagen i uka datoen ligger i. */
export function weekStart(iso: string): string {
  const dow = utcDay(iso).getUTCDay();
  return addDays(iso, dow === 0 ? -6 : 1 - dow);
}

/** ISO-ukenummer. */
export function isoWeek(iso: string): number {
  const date = utcDay(iso);
  const thursday = addDays(iso, 3 - ((date.getUTCDay() + 6) % 7));
  const yearStart = Date.UTC(Number(thursday.slice(0, 4)), 0, 1);
  return Math.floor((utcDay(thursday).getTime() - yearStart) / (7 * 24 * 60 * 60 * 1000)) + 1;
}

/** «ma 26» */
export function dayHeader(iso: string): string {
  return `${WEEKDAYS[utcDay(iso).getUTCDay()]} ${Number(iso.slice(8, 10))}`;
}

export function isWeekend(iso: string): boolean {
  const dow = utcDay(iso).getUTCDay();
  return dow === 0 || dow === 6;
}

/** Dagene i vinduet som starter på `start`. */
export function occupancyDays(start: string, days: number): string[] {
  return eachDay(start, addDays(start, days - 1));
}

const lineLabel = (l: CellLine) => l.project_code ?? l.title ?? "Internt";

/**
 * Én rad i belegget. Enkeltutstyr viser prosjektnummer der bookingen starter
 * (eller på første dag i vinduet), mengdevare viser antall booket hver dag.
 * Service skraveres på dager uten booking.
 */
export function occupancyRow(item: CellItem, lines: CellLine[], days: string[]): OccupancyCell[] {
  const own = lines.filter((l) => l.item_id === item.id);
  return days.map((day, index) => {
    const covering = own.filter((l) => l.date_from <= day && day <= l.date_to);
    if (covering.length === 0) {
      return { day, kind: item.status === "in_service" ? "service" : "free", label: "", bookingId: null };
    }
    const booked = covering.reduce((sum, l) => sum + l.quantity, 0);
    const bookingId = covering[0].booking_id;
    if (booked > item.quantity) return { day, kind: "conflict", label: `⚠ ${covering.length}`, bookingId };
    if (item.quantity > 1) return { day, kind: "booked", label: String(booked), bookingId };
    const line = covering[0];
    const starts = line.date_from === day || index === 0;
    return { day, kind: "booked", label: starts ? lineLabel(line) : "", bookingId };
  });
}

export interface OccupancyGroup<T> {
  departmentId: string | null;
  name: string;
  items: T[];
}

/** Grupper på avdeling i avdelingsrekkefølge, utstyr uten avdeling sist. */
export function groupByDepartment<T extends Pick<DBEquipmentItem, "department_id" | "name">>(
  items: T[],
  departments: { id: string; name: string }[],
): OccupancyGroup<T>[] {
  const groups: OccupancyGroup<T>[] = [
    ...departments.map((d) => ({ departmentId: d.id, name: d.name, items: [] as T[] })),
    { departmentId: null, name: "Uten avdeling", items: [] as T[] },
  ];
  const byId = new Map(groups.map((g) => [g.departmentId, g]));
  for (const item of items) (byId.get(item.department_id) ?? byId.get(null)!).items.push(item);
  for (const g of groups) g.items.sort((a, b) => a.name.localeCompare(b.name, "nb"));
  return groups.filter((g) => g.items.length > 0);
}

/** Utstyr som vises i belegget: bookbart og enten tilgjengelig eller på service. */
export function showsInOccupancy(
  item: Pick<DBEquipmentItem, "status" | "bookable" | "category_id">,
  categoryBookable: Map<string, boolean>,
): boolean {
  if (item.status !== "ok" && item.status !== "in_service") return false;
  return item.bookable ?? categoryBookable.get(item.category_id) ?? true;
}

export interface UpcomingLists<T> {
  today: T[];
  nextWeek: T[];
}

/** Mobil: «Booket i dag» og «Neste 7 dager» (starter i morgen til og med om en uke). */
export function upcomingLines<T extends Pick<DBEquipmentBookingLine, "date_from" | "date_to">>(lines: T[], today: string): UpcomingLists<T> {
  const weekEnd = addDays(today, 7);
  const byStart = [...lines].sort((a, b) => a.date_from.localeCompare(b.date_from));
  return {
    today: byStart.filter((l) => l.date_from <= today && today <= l.date_to),
    nextWeek: byStart.filter((l) => l.date_from > today && l.date_from <= weekEnd),
  };
}

/** Vinduet som må hentes for både rutenettet og mobillisten. */
export function occupancyFetchRange(gridStart: string, gridDays: number, today: string): DateRange {
  const gridEnd = addDays(gridStart, gridDays - 1);
  const weekEnd = addDays(today, 7);
  return { from: gridStart < today ? gridStart : today, to: gridEnd > weekEnd ? gridEnd : weekEnd };
}
